import React, {lazy, Suspense, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {fireGtmEvent} from '../utils/marketing.utils';

const CopilotChat = lazy(() => import('./CopilotChat'));
const DRAWER_WIDTH = 392;
const DRAWER_HEADER_HEIGHT = 56;
const AI_ICON_PATH = 'M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z';
const AI_SUGGESTION_IDS = [
  'ai.suggestion.coverageDifference',
  'ai.suggestion.thailand',
  'ai.suggestion.extremeSports',
  'ai.suggestion.familyCost',
];
const AI_COMPANION_CSS = [
  '.wobi-ai-chat{height:100%}',
  '@keyframes wobiAiIn{from{opacity:0;transform:translateY(18px) scale(.9)}to{opacity:1;transform:none}}',
  '@keyframes wobiAiGlow{0%,100%{box-shadow:0 10px 30px rgba(247,148,30,.34),' +
    '0 4px 14px rgba(255,140,50,.3)}50%{box-shadow:0 14px 40px rgba(247,148,30,.55),' +
    '0 6px 20px rgba(255,140,50,.45)}}',
  '@media (max-width:680px){',
  '.wobi-ai-fab{display:none !important}',
  '.wobi-ai-widget{left:16px !important;right:16px !important;bottom:16px !important}',
  '.wobi-ai-widget .wobi-ai-widget-inner{width:100% !important}',
  '}',
].join('');

const styles = {
  drawer: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 10000,
    width: 'min(392px, 94vw)',
    background: '#fff',
    borderRight: '1px solid #e5eaef',
    boxShadow: '6px 0 32px rgba(8,52,65,.16)',
    display: 'flex',
    flexDirection: 'column',
    direction: 'rtl',
    overflow: 'hidden',
    transition: 'transform .32s cubic-bezier(.4,0,.2,1)',
  },
  drawerHeader: {
    minHeight: DRAWER_HEADER_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    direction: 'ltr',
    padding: '8px 10px',
    borderBottom: '1px solid #dbe4ec',
    background: '#fff',
  },
  drawerHeaderTitleWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    pointerEvents: 'none',
    direction: 'rtl',
    gap: 8,
  },
  drawerHeaderTitle: {
    fontFamily: 'inherit',
    fontSize: 17,
    fontWeight: 800,
    color: 'var(--text-strong)',
    lineHeight: 1,
  },
  drawerTitleIconBadge: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'linear-gradient(135deg,var(--brand, #1f7ea7) 0%,#0a5f80 100%)',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
    pointerEvents: 'auto',
    boxShadow: '0 1px 2px rgba(7,38,57,.2)',
  },
  drawerTitleIcon: {
    width: 18,
    height: 18,
    display: 'block',
  },
  drawerClose: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    color: '#98a3b0',
    background: '#eceff2',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    flex: 'none',
    pointerEvents: 'auto',
    boxShadow: '0 1px 2px rgba(7,38,57,.1)',
  },
  chatViewport: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    background: '#fff',
  },
  disclaimer: {
    flex: 'none',
    margin: 0,
    padding: '8px 16px 12px',
    background: '#fff',
    color: '#5b6673',
    fontSize: 12,
    lineHeight: 1.5,
    textAlign: 'center',
  },
  companion: {
    position: 'fixed',
    bottom: 22,
    left: 22,
    zIndex: 9999,
    border: 'none',
    cursor: 'pointer',
    padding: 2,
    borderRadius: 999,
    background: 'linear-gradient(100deg,#ffc06a 0%,#f7941e 55%,#ff7a2f 100%)',
    backgroundSize: '180% 180%',
    animation: 'wobiAiIn .55s cubic-bezier(.22,.61,.36,1), wobiAiGlow 3.4s ease-in-out infinite',
  },
  companionInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: '#fff',
    borderRadius: 999,
    padding: '12px 20px',
  },
  companionText: {
    fontWeight: 700,
    fontSize: 15.5,
    color: '#1b2431',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '55vw',
  },
};

const AiCompanion = () => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [showSuggestion, setShowSuggestion] = useState(true);
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_WIDTH);
  const [agentTitle, setAgentTitle] = useState(() => intl.formatMessage({id: 'ai.ask'}));

  useEffect(() => {
    if (open) {
      setChatStarted(true);
    }
  }, [open]);

  useEffect(() => {
    const configuredTitle = process.env.REACT_APP_COPILOT_STUDIO_AGENT_TITLE;
    if (configuredTitle) {
      setAgentTitle(configuredTitle);
    }
  }, []);

  useEffect(() => {
    let animationTimer = null;
    const timer = setInterval(() => {
      setShowSuggestion(false);
      animationTimer = setTimeout(() => {
        setSuggestionIndex((index) => (index + 1) % AI_SUGGESTION_IDS.length);
        setShowSuggestion(true);
      }, 320);
    }, 5200);

    return () => {
      clearInterval(timer);
      if (animationTimer) {
        clearTimeout(animationTimer);
      }
    };
  }, []);

  useEffect(() => {
    const openAi = () => setOpen(true);
    window.addEventListener('wobi-open-ai', openAi);
    return () => window.removeEventListener('wobi-open-ai', openAi);
  }, []);

  useEffect(() => {
    const updateDrawerWidth = () => {
      setDrawerWidth(Math.min(DRAWER_WIDTH, Math.round(window.innerWidth * 0.94)));
    };
    updateDrawerWidth();
    window.addEventListener('resize', updateDrawerWidth);
    return () => window.removeEventListener('resize', updateDrawerWidth);
  }, []);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) {
      return undefined;
    }
    root.style.transition = 'margin-left .32s cubic-bezier(.4,0,.2,1)';
    root.style.marginLeft = open ? `${drawerWidth}px` : '0px';
    return () => {
      root.style.marginLeft = '0px';
    };
  }, [open, drawerWidth]);

  return (
    <React.Fragment>
      <style>{AI_COMPANION_CSS}</style>
      <aside
        style={{
          ...styles.drawer,
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
        }}
        aria-hidden={!open}
      >
        <div style={styles.drawerHeader}>
          <button
            style={styles.drawerClose}
            onClick={() => setOpen(false)}
            aria-label={intl.formatMessage({id: 'ai.closeChat'})}
            type='button'
          >
            <svg width='18' height='18' viewBox='0 0 24 24' aria-hidden='true'>
              <path d='M18 6 6 18' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
              <path d='M6 6l12 12' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            </svg>
          </button>
          <span style={styles.drawerHeaderTitleWrap}>
            <span style={styles.drawerTitleIconBadge} aria-hidden='true'>
              <svg
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                style={styles.drawerTitleIcon}
              >
                <path d={AI_ICON_PATH}/>
              </svg>
            </span>
            <span style={styles.drawerHeaderTitle}>{agentTitle}</span>
          </span>
        </div>
        <div style={styles.chatViewport} data-testid='ai-chat'>
          {chatStarted && (
            <Suspense fallback={<p role='status'>{intl.formatMessage({id: 'ai.loading'})}</p>}>
              <CopilotChat/>
            </Suspense>
          )}
        </div>
        <p style={styles.disclaimer} data-testid='ai-disclaimer'>
          {intl.formatMessage({id: 'ai.disclaimer'})}
        </p>
      </aside>

      {open ? null : (
        <button
          className='wobi-ai-fab'
          style={styles.companion}
          onClick={() => {
            fireGtmEvent('TravelAIAssistent');
            setOpen(true);
          }}
          aria-label={intl.formatMessage({id: 'ai.openSuggestion'})}
          type='button'
        >
          <span style={styles.companionInner}>
            <span
              className='wobi-ai-txt'
              style={{
                ...styles.companionText,
                opacity: showSuggestion ? 1 : 0,
                transform: showSuggestion ? 'none' : 'translateY(-6px)',
                transition: 'opacity .3s ease, transform .3s ease',
              }}
            >
              {intl.formatMessage({id: AI_SUGGESTION_IDS[suggestionIndex]})}
            </span>
            <span style={{display: 'flex', flex: 'none', color: '#1b2431'}}>
              <svg width='22' height='22' viewBox='0 0 24 24' aria-hidden='true'>
                <path d={AI_ICON_PATH} fill='currentColor'/>
              </svg>
            </span>
          </span>
        </button>
      )}
    </React.Fragment>
  );
};

export default AiCompanion;
